import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Lock, Upload, FileSpreadsheet, CheckCircle, XCircle, History, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { importacionesApi } from '../lib/api';

const CLAVE = '2020';

// Normaliza nombres de columnas para no depender de mayusculas/acentos exactos del Excel
function normalizar(s: string) {
  return s.toString().trim().toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function campo(fila: any, candidatos: string[]) {
  const claves = Object.keys(fila);
  for (const candidato of candidatos) {
    const encontrada = claves.find(k => normalizar(k) === normalizar(candidato));
    if (encontrada !== undefined && fila[encontrada] !== '') return fila[encontrada];
  }
  return undefined;
}

interface FilaVenta {
  fecha: string;
  descripcion: string;
  monto: number;
  capital: number;
  ganancia: number;
}

const ImportarDatos = () => {
  const [desbloqueado, setDesbloqueado] = useState(false);
  const [claveIngresada, setClaveIngresada] = useState('');
  const [errorClave, setErrorClave] = useState('');

  const [archivo, setArchivo] = useState<File | null>(null);
  const [hojaGanancia, setHojaGanancia] = useState<FilaVenta[] | null>(null);
  const [otrasFilas, setOtrasFilas] = useState<any[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [importadoComoVentas, setImportadoComoVentas] = useState<any>(null);
  const [historial, setHistorial] = useState<any[]>([]);

  useEffect(() => {
    if (desbloqueado) cargarHistorial();
  }, [desbloqueado]);

  const cargarHistorial = async () => {
    try {
      const data = await importacionesApi.getAll();
      setHistorial(data || []);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  const intentarDesbloquear = (e: React.FormEvent) => {
    e.preventDefault();
    if (claveIngresada === CLAVE) {
      setDesbloqueado(true);
      setErrorClave('');
    } else {
      setErrorClave('Clave incorrecta');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setArchivo(file);
    setResultado(null);
    setImportadoComoVentas(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target?.result;
      const workbook = XLSX.read(data, { type: 'binary', cellDates: true });

      const nombreHojaGanancia = workbook.SheetNames.find(n => normalizar(n).includes('GANANCIA'));

      if (nombreHojaGanancia) {
        // La hoja de ganancia tiene un titulo en la fila 1 y los encabezados reales en la fila 2
        const hoja = workbook.Sheets[nombreHojaGanancia];
        const filasCrudas = XLSX.utils.sheet_to_json(hoja, { defval: '', range: 1 }) as any[];
        const filasVenta: FilaVenta[] = filasCrudas
          .map(f => {
            const fecha = campo(f, ['FECHA']);
            const descripcion = campo(f, ['DESCRIPCION', 'DESCRIPCIÓN']);
            const monto = parseFloat(campo(f, ['MONTO']) || 0);
            const capital = parseFloat(campo(f, ['CAPITAL']) || 0);
            return {
              fecha: fecha instanceof Date ? fecha.toISOString() : String(fecha || ''),
              descripcion: String(descripcion || ''),
              monto,
              capital,
              ganancia: monto - capital
            };
          })
          // saca filas de titulo/saldo inicial sin producto real o sin monto de venta
          .filter(f => f.descripcion && f.descripcion.toUpperCase() !== 'DESCRIPCIÓN' && f.monto > 0);
        setHojaGanancia(filasVenta);
        setOtrasFilas([]);
      } else {
        // Sin hoja "GANANCIA": comportamiento generico (primera hoja, identificar contra stock)
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        setOtrasFilas(json as any[]);
        setHojaGanancia(null);
      }
    };
    reader.readAsBinaryString(file);
  };

  const totales = hojaGanancia
    ? hojaGanancia.reduce((acc, f) => ({ monto: acc.monto + f.monto, ganancia: acc.ganancia + f.ganancia }), { monto: 0, ganancia: 0 })
    : null;

  const identificarContraStock = async () => {
    if (!archivo) return;
    setProcesando(true);
    try {
      const filasParaIdentificar = hojaGanancia
        ? hojaGanancia.map(f => ({ descripcion: f.descripcion }))
        : otrasFilas;
      const res = await importacionesApi.crear({
        clave: CLAVE,
        nombre_archivo: archivo.name,
        filas: filasParaIdentificar
      });
      setResultado(res);
      toast.success(`Identificados ${res.coincidencias} de ${res.total_filas} contra el catálogo`);
      cargarHistorial();
    } catch (error: any) {
      console.error('Error importando:', error);
      toast.error('Error al importar: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  const confirmarImportarComoVentas = async () => {
    if (!hojaGanancia || hojaGanancia.length === 0) return;
    if (!confirm(`¿Confirmás crear ${hojaGanancia.length} ventas históricas por un total de S/ ${totales?.monto.toFixed(2)}? No se va a descontar stock (son ventas ya pasadas).`)) return;

    setProcesando(true);
    try {
      const filasConProducto = hojaGanancia.map((f, i) => ({
        ...f,
        producto_id: resultado?.filas?.[i]?._identificado?.producto_id || null
      }));
      const res = await importacionesApi.convertirAVentas({ clave: CLAVE, filas: filasConProducto });
      setImportadoComoVentas(res);
      toast.success(`${res.insertadas} ventas creadas — Ganancia total S/ ${res.sumaGanancia.toFixed(2)}`);
    } catch (error: any) {
      console.error('Error convirtiendo en ventas:', error);
      toast.error('Error al crear las ventas: ' + error.message);
    } finally {
      setProcesando(false);
    }
  };

  if (!desbloqueado) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <form onSubmit={intentarDesbloquear} className="bg-white rounded-lg shadow-md p-8 w-full max-w-sm text-center">
          <Lock className="mx-auto text-gray-400 mb-4" size={40} />
          <h1 className="text-xl font-bold mb-2">Importar Datos</h1>
          <p className="text-gray-500 text-sm mb-6">Esta sección requiere una clave de acceso</p>
          <input
            type="password"
            value={claveIngresada}
            onChange={(e) => setClaveIngresada(e.target.value)}
            placeholder="Clave de acceso"
            className="w-full border rounded-lg px-3 py-2 text-center mb-3"
            autoFocus
          />
          {errorClave && <p className="text-red-600 text-sm mb-3">{errorClave}</p>}
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium">
            Ingresar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <FileSpreadsheet className="text-green-600" />
          Importar Datos (xlsx)
        </h1>
        <p className="text-gray-600 mt-1">
          Si el Excel tiene una hoja "GANANCIA" (fecha, descripción, monto, capital), se arma una vista previa para cargarla como ventas históricas.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
        >
          {archivo ? (
            <>
              <FileSpreadsheet size={40} className="text-green-500 mb-2" />
              <p className="font-semibold text-gray-700">{archivo.name}</p>
              <p className="text-sm text-gray-500 mt-1">
                {hojaGanancia ? `Hoja "GANANCIA" detectada — ${hojaGanancia.length} ventas` : `${otrasFilas.length} filas detectadas`}
              </p>
            </>
          ) : (
            <>
              <Upload size={40} className="text-gray-400 mb-2" />
              <p className="font-semibold text-gray-700">Click para seleccionar archivo Excel</p>
              <p className="text-xs text-gray-400 mt-1">.xlsx, .xls</p>
            </>
          )}
          <input id="file-upload" type="file" className="hidden" accept=".xlsx,.xls" onChange={handleFileChange} />
        </label>

        {archivo && (
          <button
            onClick={identificarContraStock}
            disabled={procesando}
            className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {procesando ? 'Procesando...' : 'Identificar contra el catálogo de Stock'}
          </button>
        )}
      </div>

      {hojaGanancia && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="text-green-600" size={20} /> Vista previa — Ventas de "GANANCIA"
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Filas</p>
              <p className="text-2xl font-bold">{hojaGanancia.length}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Monto total</p>
              <p className="text-2xl font-bold text-green-600">S/ {totales?.monto.toFixed(2)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Ganancia total</p>
              <p className="text-2xl font-bold text-blue-600">S/ {totales?.ganancia.toFixed(2)}</p>
            </div>
          </div>

          <div className="overflow-x-auto max-h-80 mb-4">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Descripción</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-right">Capital</th>
                  <th className="px-3 py-2 text-right">Ganancia</th>
                  {resultado && <th className="px-3 py-2 text-center">Catálogo</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {hojaGanancia.map((f, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{f.fecha ? new Date(f.fecha).toLocaleDateString('es-PE') : '-'}</td>
                    <td className="px-3 py-2">{f.descripcion}</td>
                    <td className="px-3 py-2 text-right">S/ {f.monto.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">S/ {f.capital.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-green-600">S/ {f.ganancia.toFixed(2)}</td>
                    {resultado && (
                      <td className="px-3 py-2 text-center">
                        {resultado.filas?.[i]?._identificado?.encontrado
                          ? <span className="text-green-600 text-xs">✓</span>
                          : <span className="text-yellow-600 text-xs">?</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!importadoComoVentas ? (
            <button
              onClick={confirmarImportarComoVentas}
              disabled={procesando}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
            >
              {procesando ? 'Importando...' : `Confirmar e Importar como ${hojaGanancia.length} Ventas`}
            </button>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="mx-auto text-green-600 mb-2" size={28} />
              <p className="font-semibold text-green-800">
                {importadoComoVentas.insertadas} ventas creadas — Ganancia total S/ {importadoComoVentas.sumaGanancia.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      )}

      {resultado && !hojaGanancia && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Resultado</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700"><CheckCircle size={20} /><span className="font-semibold">Identificados</span></div>
              <p className="text-3xl font-bold text-green-600">{resultado.coincidencias}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-700"><XCircle size={20} /><span className="font-semibold">Sin identificar</span></div>
              <p className="text-3xl font-bold text-yellow-600">{resultado.total_filas - resultado.coincidencias}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><History size={20} /> Importaciones anteriores</h3>
        {historial.length === 0 ? (
          <p className="text-gray-500 text-sm">Todavía no hay importaciones guardadas</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Archivo</th>
                <th className="px-3 py-2 text-center">Filas</th>
                <th className="px-3 py-2 text-center">Identificados</th>
                <th className="px-3 py-2 text-left">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {historial.map((h) => (
                <tr key={h.id}>
                  <td className="px-3 py-2">{h.nombre_archivo}</td>
                  <td className="px-3 py-2 text-center">{h.total_filas}</td>
                  <td className="px-3 py-2 text-center">{h.coincidencias}</td>
                  <td className="px-3 py-2">{new Date(h.fecha).toLocaleString('es-PE')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ImportarDatos;
